import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';

const Skills = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const sectionBg = isDark ? '#161b22' : '#f0f6fc';
  const paperBg = isDark ? '#0d1117' : '#ffffff';
  const textColor = theme.palette.text.primary;
  const accentRed = '#ff0033';
  const accentCyan = '#00ffff';
  const progressBg = isDark ? '#1f2937' : '#e0e0e0';
  const chipBg = progressBg;
  const chipBorderRed = `1px solid ${accentRed}`;
  const chipBorderCyan = `1px solid ${accentCyan}`;
  const paperBorderRed = `1px solid rgba(255, 0, 51, 0.2)`;
  const paperBorderCyan = `1px solid rgba(0, 255, 255, 0.2)`;
  const paperShadowRed = `0 4px 20px rgba(255, 0, 51, 0.1)`;
  const paperShadowCyan = `0 4px 20px rgba(0, 255, 255, 0.1)`;

  return (
    <Box
      component="section"
      id="skills"
      sx={{ bgcolor: sectionBg, py: 10 }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          sx={{ fontFamily: "JetBrains Mono, monospace", fontSize: { xs: "32px", md: "48px" }, fontWeight: 700, color: accentRed, mb: 6, textAlign: "center" }}
        >
          <span style={{ color: accentCyan }}>$</span>
          <span> skills --list</span>
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper
              sx={{ bgcolor: paperBg, p: 4, border: paperBorderRed, boxShadow: paperShadowRed }}
            >
              <Typography
                variant="h5"
                sx={{ fontFamily: "JetBrains Mono, monospace", color: accentCyan, mb: 3, fontSize: "20px" }}
              >
                Languages
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography
                    sx={{ fontFamily: "JetBrains Mono, monospace", fontSize: "14px", color: textColor }}
                  >
                    Java (Swing & Gradle)
                  </Typography>
                  <Typography
                    sx={{ fontFamily: "JetBrains Mono, monospace", fontSize: "14px", color: accentRed }}
                  >
                    95%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={95}
                  sx={{ height: 8, bgcolor: progressBg, "& .MuiLinearProgress-bar": { bgcolor: accentRed } }}
                />
              </Box>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography
                    sx={{ fontFamily: "JetBrains Mono, monospace", fontSize: "14px", color: textColor }}
                  >
                    JavaScript / TypeScript
                  </Typography>
                  <Typography
                    sx={{ fontFamily: "JetBrains Mono, monospace", fontSize: "14px", color: accentRed }}
                  >
                    95%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={95}
                  sx={{ height: 8, bgcolor: progressBg, "& .MuiLinearProgress-bar": { bgcolor: accentRed } }}
                />
              </Box>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography
                    sx={{ fontFamily: "JetBrains Mono, monospace", fontSize: "14px", color: textColor }}
                  >
                    Python
                  </Typography>
                  <Typography
                    sx={{ fontFamily: "JetBrains Mono, monospace", fontSize: "14px", color: accentRed }}
                  >
                    90%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={90}
                  sx={{ height: 8, bgcolor: progressBg, "& .MuiLinearProgress-bar": { bgcolor: accentRed } }}
                />
              </Box>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography
                    sx={{ fontFamily: "JetBrains Mono, monospace", fontSize: "14px", color: textColor }}
                  >
                    Rust
                  </Typography>
                  <Typography
                    sx={{ fontFamily: "JetBrains Mono, monospace", fontSize: "14px", color: accentRed }}
                  >
                    75%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={75}
                  sx={{ height: 8, bgcolor: progressBg, "& .MuiLinearProgress-bar": { bgcolor: accentRed } }}
                />
              </Box>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography
                    sx={{ fontFamily: "JetBrains Mono, monospace", fontSize: "14px", color: textColor }}
                  >
                    Go
                  </Typography>
                  <Typography
                    sx={{ fontFamily: "JetBrains Mono, monospace", fontSize: "14px", color: accentRed }}
                  >
                    70%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={70}
                  sx={{ height: 8, bgcolor: progressBg, "& .MuiLinearProgress-bar": { bgcolor: accentRed } }}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              sx={{ bgcolor: paperBg, p: 4, border: paperBorderCyan, boxShadow: paperShadowCyan }}
            >
              <Typography
                variant="h5"
                sx={{ fontFamily: "JetBrains Mono, monospace", color: accentCyan, mb: 3, fontSize: "20px" }}
              >
                Frameworks & Tools
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                <Chip
                  label="React"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="Next.js"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="Node.js"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="Express"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="Django"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="FastAPI"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="GraphQL"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="PostgreSQL"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="MongoDB"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="Redis"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              sx={{ bgcolor: paperBg, p: 4, border: paperBorderRed, boxShadow: paperShadowRed }}
            >
              <Typography
                variant="h5"
                sx={{ fontFamily: "JetBrains Mono, monospace", color: accentCyan, mb: 3, fontSize: "20px" }}
              >
                DevOps & Cloud
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                <Chip
                  label="Docker"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderRed }}
                />
                <Chip
                  label="Kubernetes"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderRed }}
                />
                <Chip
                  label="AWS"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderRed }}
                />
                <Chip
                  label="GCP"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderRed }}
                />
                <Chip
                  label="Terraform"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderRed }}
                />
                <Chip
                  label="CI/CD"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderRed }}
                />
                <Chip
                  label="GitHub Actions"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderRed }}
                />
                <Chip
                  label="Nginx"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderRed }}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              sx={{ bgcolor: paperBg, p: 4, border: paperBorderCyan, boxShadow: paperShadowCyan }}
            >
              <Typography
                variant="h5"
                sx={{ fontFamily: "JetBrains Mono, monospace", color: accentCyan, mb: 3, fontSize: "20px" }}
              >
                Pentesting & Security
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                <Chip
                  label="Burp Suite"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="Metasploit"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="Nmap"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="Wireshark"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="Ghidra"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="IDA Pro"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="SQLMap"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="OWASP ZAP"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="Cobalt Strike"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
                <Chip
                  label="John the Ripper"
                  sx={{ bgcolor: chipBg, color: textColor, fontFamily: "JetBrains Mono, monospace", border: chipBorderCyan }}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Skills;